import { useRef, useEffect, useCallback, useState } from 'react';
import { useDock } from '../Dock/DockContext';
import './unified-panels-svg.css';

/**
 * =============================================================================
 * UNIFIED PANELS SVG - VORTEX-CENTRIC LAYOUT
 * =============================================================================
 *
 * This component renders ALL 4 panel shapes in a single SVG that spans the
 * full viewport. All panels curve around THREE floating islands:
 *
 * 1. BRANDING PILL (top) - "Driver | Gauge" text
 * 2. VORTEX (middle) - Profile picture circle
 * 3. DOCK (bottom) - Navigation dock
 *
 * The negative space flows continuously from top to bottom, creating a unified
 * visual channel connecting all three elements.
 *
 * =============================================================================
 */

/**
 * Configuration for the unified panel system
 *
 * Based on prototype-vortex-layout.html:
 * - Viewport: 1200 x 800
 * - Branding: 200x36, top=8, cornerRadius=18, padding=10
 * - Vortex: 160px diameter, center at (600, 180), padding=15 -> effectiveRadius=95
 * - Dock: 400x56, center at (600, 744), cornerRadius=20, padding=10 -> wrapRadius=30
 * - Stem: 20px wide (x=590 to x=610)
 */
const CONFIG = {
  // Viewport margins - minimal but non-zero spacing from edges
  margin: {
    top: 8,
    right: 8,
    bottom: 8,
    left: 8,
  },
  // Branding pill - matches prototype
  branding: {
    width: 200,
    height: 36,
    topOffset: 8, // This is relative to margin.top
    cornerRadius: 18,
    padding: 10,
  },
  // Vortex (profile portal) - matches prototype
  vortex: {
    padding: 15, // Creates 95px effective radius from 80px base
    topOffset: 100, // Vortex top edge at y=100 (center at y=180)
  },
  // Stem (gap between panels) - matches prototype
  stem: {
    halfWidth: 10, // 20px total stem width (x=centerX-10 to centerX+10)
  },
  // Panel corners
  panel: {
    cornerRadius: 16,
  },
  // Dock - cleaner wrap-around, matches prototype
  dock: {
    padding: 10, // Matches prototype padding
    hoverPadding: 6,
    hoverScale: 1.06,
    wrapRadius: 32, // Smooth curve radius for wrap-around
  },
} as const;

/**
 * Helper to get computed CSS variable value in pixels
 */
function getCSSVar(name: string, fallback: number): number {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!value) {return fallback;}
  if (value.endsWith('rem')) {return parseFloat(value) * 16;}
  if (value.endsWith('px')) {return parseFloat(value);}
  return fallback;
}

/**
 * Calculate vortex geometry based on CSS variables
 *
 * Vortex positioning - percentage-based to match CSS:
 *
 * From prototype (1200x800 viewport):
 * - Vortex: 160px diameter, top edge at 100px (12.5% of 800px)
 * - Vortex center: y=180px (22.5% of 800px)
 * - With padding (15px): effective radius = 95px
 *
 * This positions the vortex below the branding area with proper spacing.
 * Headers wrap HORIZONTALLY around the vortex (like panels wrap around dock).
 */
function getVortexGeometry(_centerX: number, viewportHeight: number): {
  centerY: number;
  radius: number;
  effectiveRadius: number;
} {
  const diameter = getCSSVar('--vortex-diameter', 160);
  const radius = diameter / 2;

  // Percentage-based positioning: vortex center at 22.5% of viewport height
  // This matches: vortex top at 12.5% + radius
  // On 800px viewport: 0.225 * 800 = 180px (matching prototype)
  const centerY = viewportHeight * 0.225;

  return {
    centerY,
    radius,
    effectiveRadius: radius + CONFIG.vortex.padding,
  };
}

/**
 * Calculate where a vertical line intersects a circle
 */
function circleIntersectionY(
  circleX: number,
  circleY: number,
  radius: number,
  lineX: number
): { top: number; bottom: number } {
  const dx = lineX - circleX;
  if (Math.abs(dx) > radius) {
    // Line doesn't intersect circle
    return { top: circleY - radius, bottom: circleY + radius };
  }
  const dy = Math.sqrt(radius * radius - dx * dx);
  return {
    top: circleY - dy,
    bottom: circleY + dy,
  };
}

interface UnifiedPanelsSVGProps {
  /** Ref to the vortex element for position tracking */
  vortexRef?: React.RefObject<HTMLElement>;
}

export function UnifiedPanelsSVG({ vortexRef }: UnifiedPanelsSVGProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Path refs for each panel
  const headerLeftRef = useRef<SVGPathElement>(null);
  const headerRightRef = useRef<SVGPathElement>(null);
  const notesRef = useRef<SVGPathElement>(null);
  const tasksRef = useRef<SVGPathElement>(null);

  const { isDockHovered } = useDock();
  const [ready, setReady] = useState(false);

  // Store base dock dimensions (measured once when not hovered)
  const baseDockDimensions = useRef<{
    width: number;
    height: number;
    cornerRadius: number;
  } | null>(null);

  /**
   * Initialize paths immediately with simple fallback shapes.
   * This ensures panels are visible before dock measurements are available.
   */
  const initializeFallbackPaths = useCallback(() => {
    if (!containerRef.current || !svgRef.current) {return;}
    if (!headerLeftRef.current || !headerRightRef.current) {return;}
    if (!notesRef.current || !tasksRef.current) {return;}

    const containerRect = containerRef.current.getBoundingClientRect();
    const W = containerRect.width;
    const H = containerRect.height;
    const centerX = W / 2;

    // Update viewBox
    svgRef.current.setAttribute('viewBox', `0 0 ${W} ${H}`);

    // Simple rectangular fallback paths (no dock cutout)
    const margin = 8;
    const r = 16; // corner radius
    const vortexY = H * 0.225;
    const stemHalf = 10;

    // Simple header left path
    headerLeftRef.current.setAttribute('d', `
      M ${margin + r},${margin}
      L ${centerX - stemHalf},${margin}
      L ${centerX - stemHalf},${vortexY}
      L ${margin},${vortexY}
      L ${margin},${margin + r}
      Q ${margin},${margin} ${margin + r},${margin}
      Z
    `);

    // Simple header right path
    headerRightRef.current.setAttribute('d', `
      M ${centerX + stemHalf},${margin}
      L ${W - margin - r},${margin}
      Q ${W - margin},${margin} ${W - margin},${margin + r}
      L ${W - margin},${vortexY}
      L ${centerX + stemHalf},${vortexY}
      L ${centerX + stemHalf},${margin}
      Z
    `);

    // Simple notes path (left panel)
    notesRef.current.setAttribute('d', `
      M ${margin},${vortexY}
      L ${centerX - stemHalf},${vortexY}
      L ${centerX - stemHalf},${H - margin}
      L ${margin + r},${H - margin}
      Q ${margin},${H - margin} ${margin},${H - margin - r}
      L ${margin},${vortexY}
      Z
    `);

    // Simple tasks path (right panel)
    tasksRef.current.setAttribute('d', `
      M ${W - margin},${vortexY}
      L ${centerX + stemHalf},${vortexY}
      L ${centerX + stemHalf},${H - margin}
      L ${W - margin - r},${H - margin}
      Q ${W - margin},${H - margin} ${W - margin},${H - margin - r}
      L ${W - margin},${vortexY}
      Z
    `);
  }, []);

  // Initialize fallback paths immediately when component mounts
  useEffect(() => {
    // Use requestAnimationFrame to ensure refs are populated
    requestAnimationFrame(() => {
      initializeFallbackPaths();
    });
  }, [initializeFallbackPaths]);

  // Wait for dock to be available for precise paths
  useEffect(() => {
    const checkReady = (): boolean => {
      const dock = document.querySelector('.dock-container');
      if (dock && containerRef.current) {
        setReady(true);
        return true;
      }
      return false;
    };

    if (!checkReady()) {
      [100, 200, 500, 1000].forEach((delay) => {
        setTimeout(checkReady, delay);
      });
    }
  }, []);

  /**
   * Calculate and update all SVG paths with precise dock measurements
   */
  const updatePaths = useCallback(() => {
    if (!containerRef.current || !svgRef.current) {return;}
    if (!headerLeftRef.current || !headerRightRef.current) {return;}
    if (!notesRef.current || !tasksRef.current) {return;}

    const dockElement = document.querySelector('.dock-container');
    if (!dockElement) {return;}

    // Get container dimensions
    const containerRect = containerRef.current.getBoundingClientRect();
    const W = containerRect.width;
    const H = containerRect.height;
    const centerX = W / 2;

    // Margin insets - panels start/end inward from edges
    const marginTop = CONFIG.margin.top;
    const marginRight = CONFIG.margin.right;
    const marginBottom = CONFIG.margin.bottom;
    const marginLeft = CONFIG.margin.left;

    // Effective drawing bounds (with margins applied)
    const drawLeft = marginLeft;
    const drawRight = W - marginRight;
    const drawTop = marginTop;
    const drawBottom = H - marginBottom;

    // Update SVG viewBox
    svgRef.current.setAttribute('viewBox', `0 0 ${W} ${H}`);

    // =========================================================================
    // BRANDING GEOMETRY
    // =========================================================================
    const brandingWidth = CONFIG.branding.width;
    const brandingHeight = CONFIG.branding.height;
    const brandingTop = CONFIG.branding.topOffset;
    const brandingBottom = brandingTop + brandingHeight;
    const brandingPadding = CONFIG.branding.padding;
    const brandingCornerRadius = CONFIG.branding.cornerRadius;
    const brandingWrapRadius = brandingCornerRadius + brandingPadding;

    // Branding padded bounds
    const brandingPaddedBottom = brandingBottom + brandingPadding;
    const brandingPaddedLeft = centerX - brandingWidth / 2 - brandingPadding;
    const brandingPaddedRight = centerX + brandingWidth / 2 + brandingPadding;

    // =========================================================================
    // VORTEX GEOMETRY
    // =========================================================================
    const vortex = getVortexGeometry(centerX, H);
    const stemHalf = CONFIG.stem.halfWidth;
    const stemLeft = centerX - stemHalf;
    const stemRight = centerX + stemHalf;

    // Where stem edges intersect the vortex circle
    const vortexIntersectLeft = circleIntersectionY(
      centerX,
      vortex.centerY,
      vortex.effectiveRadius,
      stemLeft
    );
    const vortexIntersectRight = circleIntersectionY(
      centerX,
      vortex.centerY,
      vortex.effectiveRadius,
      stemRight
    );

    // Vortex cardinal points (with padding)
    const vortexLeft = centerX - vortex.effectiveRadius;
    const vortexRight = centerX + vortex.effectiveRadius;

    // Header/Content split at vortex center line
    const splitY = vortex.centerY;

    // =========================================================================
    // DOCK GEOMETRY
    // =========================================================================
    // Store base dock size when not hovered
    if (!baseDockDimensions.current && !isDockHovered) {
      const dockRect = dockElement.getBoundingClientRect();
      const dockStyles = window.getComputedStyle(dockElement);
      baseDockDimensions.current = {
        width: dockRect.width,
        height: dockRect.height,
        cornerRadius: parseFloat(dockStyles.borderRadius) || 24,
      };
    }

    // Fallback if hovered on first render
    if (!baseDockDimensions.current) {
      const dockRect = dockElement.getBoundingClientRect();
      const dockStyles = window.getComputedStyle(dockElement);
      baseDockDimensions.current = {
        width: dockRect.width / CONFIG.dock.hoverScale,
        height: dockRect.height / CONFIG.dock.hoverScale,
        cornerRadius: parseFloat(dockStyles.borderRadius) || 24,
      };
    }

    const base = baseDockDimensions.current;
    const scale = isDockHovered ? CONFIG.dock.hoverScale : 1;
    const dockPadding = isDockHovered ? CONFIG.dock.hoverPadding : CONFIG.dock.padding;

    // Fresh dock position
    const freshDockRect = dockElement.getBoundingClientRect();
    const dockCenterX = freshDockRect.left + freshDockRect.width / 2 - containerRect.left;
    const dockCenterY = freshDockRect.top + freshDockRect.height / 2 - containerRect.top;

    // Scaled dock dimensions
    const dockW = base.width * scale;
    const dockH = base.height * scale;
    const dockCornerRadius = base.cornerRadius * scale;
    const dockWrapRadius = dockCornerRadius + dockPadding;

    // Dock bounds
    const dockLeft = dockCenterX - dockW / 2;
    const dockRight = dockCenterX + dockW / 2;
    const dockTop = dockCenterY - dockH / 2;
    const dockBottom = dockCenterY + dockH / 2;

    // Dock padded bounds
    const dockPaddedTop = dockTop - dockPadding;
    const dockPaddedBottom = Math.min(dockBottom + dockPadding, H);
    const dockPaddedLeft = dockLeft - dockPadding;
    const dockPaddedRight = dockRight + dockPadding;

    // =========================================================================
    // PANEL CORNER RADIUS
    // =========================================================================
    const r = CONFIG.panel.cornerRadius;

    // =========================================================================
    // HEADER LEFT PATH
    // Curves around branding (bottom-left) and vortex (top-left quadrant)
    // Now uses margin-adjusted bounds (drawLeft, drawTop, etc.)
    // =========================================================================
    const headerLeftPath = `
      M ${brandingPaddedLeft},${drawTop}
      L ${brandingPaddedLeft},${brandingBottom - brandingCornerRadius}
      A ${brandingWrapRadius} ${brandingWrapRadius} 0 0 0 ${brandingPaddedLeft + brandingWrapRadius},${brandingPaddedBottom}
      L ${stemLeft},${brandingPaddedBottom}
      L ${stemLeft},${vortexIntersectLeft.top}
      A ${vortex.effectiveRadius} ${vortex.effectiveRadius} 0 0 0 ${vortexLeft},${splitY}
      L ${drawLeft},${splitY}
      L ${drawLeft},${drawTop + r}
      Q ${drawLeft},${drawTop} ${drawLeft + r},${drawTop}
      L ${brandingPaddedLeft},${drawTop}
      Z
    `;

    // =========================================================================
    // HEADER RIGHT PATH
    // Mirror of header left
    // =========================================================================
    const headerRightPath = `
      M ${brandingPaddedRight},${drawTop}
      L ${brandingPaddedRight},${brandingBottom - brandingCornerRadius}
      A ${brandingWrapRadius} ${brandingWrapRadius} 0 0 1 ${brandingPaddedRight - brandingWrapRadius},${brandingPaddedBottom}
      L ${stemRight},${brandingPaddedBottom}
      L ${stemRight},${vortexIntersectRight.top}
      A ${vortex.effectiveRadius} ${vortex.effectiveRadius} 0 0 1 ${vortexRight},${splitY}
      L ${drawRight},${splitY}
      L ${drawRight},${drawTop + r}
      Q ${drawRight},${drawTop} ${drawRight - r},${drawTop}
      L ${brandingPaddedRight},${drawTop}
      Z
    `;

    // =========================================================================
    // NOTES PATH (Left content panel)
    // Curves around vortex (bottom-left) and dock (left side, wrapping around)
    //
    // From prototype - clean dock wrap:
    // - Go down from vortex to dock top
    // - Curve into dock edge with single smooth arc
    // - Go down along dock side
    // - Curve out at dock bottom with single smooth arc
    // - Continue to bottom edge
    // =========================================================================
    const notesPath = `
      M ${drawLeft},${splitY}
      L ${vortexLeft},${splitY}
      A ${vortex.effectiveRadius} ${vortex.effectiveRadius} 0 0 0 ${stemLeft},${vortexIntersectLeft.bottom}
      L ${stemLeft},${dockPaddedTop}
      L ${dockPaddedLeft + dockWrapRadius},${dockPaddedTop}
      A ${dockWrapRadius} ${dockWrapRadius} 0 0 0 ${dockPaddedLeft},${dockPaddedTop + dockWrapRadius}
      L ${dockPaddedLeft},${dockPaddedBottom - dockWrapRadius}
      A ${dockWrapRadius} ${dockWrapRadius} 0 0 0 ${dockPaddedLeft + dockWrapRadius},${dockPaddedBottom}
      L ${stemLeft},${dockPaddedBottom}
      L ${stemLeft},${drawBottom}
      L ${drawLeft + r},${drawBottom}
      Q ${drawLeft},${drawBottom} ${drawLeft},${drawBottom - r}
      L ${drawLeft},${splitY}
      Z
    `;

    // =========================================================================
    // TASKS PATH (Right content panel)
    // Mirror of notes - same clean dock wrap approach
    // =========================================================================
    const tasksPath = `
      M ${drawRight},${splitY}
      L ${vortexRight},${splitY}
      A ${vortex.effectiveRadius} ${vortex.effectiveRadius} 0 0 1 ${stemRight},${vortexIntersectRight.bottom}
      L ${stemRight},${dockPaddedTop}
      L ${dockPaddedRight - dockWrapRadius},${dockPaddedTop}
      A ${dockWrapRadius} ${dockWrapRadius} 0 0 1 ${dockPaddedRight},${dockPaddedTop + dockWrapRadius}
      L ${dockPaddedRight},${dockPaddedBottom - dockWrapRadius}
      A ${dockWrapRadius} ${dockWrapRadius} 0 0 1 ${dockPaddedRight - dockWrapRadius},${dockPaddedBottom}
      L ${stemRight},${dockPaddedBottom}
      L ${stemRight},${drawBottom}
      L ${drawRight - r},${drawBottom}
      Q ${drawRight},${drawBottom} ${drawRight},${drawBottom - r}
      L ${drawRight},${splitY}
      Z
    `;

    // Apply paths
    headerLeftRef.current.setAttribute('d', headerLeftPath);
    headerRightRef.current.setAttribute('d', headerRightPath);
    notesRef.current.setAttribute('d', notesPath);
    tasksRef.current.setAttribute('d', tasksPath);

  }, [isDockHovered, vortexRef]);

  /**
   * Sync vortex geometry to CSS custom properties on document root.
   * This creates a SINGLE SOURCE OF TRUTH - JavaScript calculates geometry,
   * CSS consumes it via custom properties.
   */
  const syncGeometryToCSS = useCallback(() => {
    if (!containerRef.current) {return;}

    const containerRect = containerRef.current.getBoundingClientRect();
    const W = containerRect.width;
    const H = containerRect.height;
    const centerX = W / 2;

    // Calculate vortex geometry (same as updatePaths)
    const vortex = getVortexGeometry(centerX, H);

    // Stem geometry
    const stemHalf = CONFIG.stem.halfWidth;
    const stemWidth = stemHalf * 2;

    // Branding geometry
    const brandingPadding = CONFIG.branding.padding;
    const brandingWidth = CONFIG.branding.width;
    const brandingHeight = CONFIG.branding.height;
    const brandingTop = CONFIG.branding.topOffset;
    const brandingBottom = brandingTop + brandingHeight;
    const brandingPaddedBottom = brandingBottom + brandingPadding;

    // Set CSS custom properties on document root
    const root = document.documentElement;

    // Vortex geometry
    root.style.setProperty('--vortex-center-x', `${centerX}px`);
    root.style.setProperty('--vortex-center-y', `${vortex.centerY}px`);
    root.style.setProperty('--vortex-radius', `${vortex.radius}px`);
    root.style.setProperty('--vortex-effective-radius', `${vortex.effectiveRadius}px`);

    // Vortex percentage positioning (for responsive layouts)
    root.style.setProperty('--vortex-top-percent', `${(vortex.centerY - vortex.radius) / H * 100}%`);
    root.style.setProperty('--vortex-center-percent', `${vortex.centerY / H * 100}%`);

    // Stem geometry
    root.style.setProperty('--stem-width', `${stemWidth}px`);
    root.style.setProperty('--stem-half-width', `${stemHalf}px`);

    // Branding geometry
    root.style.setProperty('--branding-width', `${brandingWidth}px`);
    root.style.setProperty('--branding-height', `${brandingHeight}px`);
    root.style.setProperty('--branding-padded-bottom', `${brandingPaddedBottom}px`);

    // Layout margins
    root.style.setProperty('--layout-margin-top', `${CONFIG.margin.top}px`);
    root.style.setProperty('--layout-margin-right', `${CONFIG.margin.right}px`);
    root.style.setProperty('--layout-margin-bottom', `${CONFIG.margin.bottom}px`);
    root.style.setProperty('--layout-margin-left', `${CONFIG.margin.left}px`);

    // Viewport dimensions (useful for calculations)
    root.style.setProperty('--viewport-width', `${W}px`);
    root.style.setProperty('--viewport-height', `${H}px`);
  }, []);

  // Update paths when ready or hover state changes
  useEffect(() => {
    if (ready) {
      requestAnimationFrame(() => {
        updatePaths();
        syncGeometryToCSS();
      });
    }
  }, [updatePaths, syncGeometryToCSS, ready, isDockHovered]);

  // Update paths and CSS on resize
  useEffect(() => {
    const handleResize = (): void => {
      if (ready) {
        baseDockDimensions.current = null;
        updatePaths();
        syncGeometryToCSS();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updatePaths, syncGeometryToCSS, ready]);

  return (
    <div className="unified-panels" ref={containerRef}>
      <svg
        className="unified-panels-svg"
        ref={svgRef}
        viewBox="0 0 1200 800"  /* Default viewBox matching prototype - updated by updatePaths */
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {/* Header panels (slightly different fill) */}
        <path ref={headerLeftRef} className="panel-path panel-path--header" />
        <path ref={headerRightRef} className="panel-path panel-path--header" />

        {/* Content panels */}
        <path ref={notesRef} className="panel-path panel-path--notes" />
        <path ref={tasksRef} className="panel-path panel-path--tasks" />
      </svg>
    </div>
  );
}

export default UnifiedPanelsSVG;
